import { apiClient } from './api';
import {
  SavedDraft,
  SavedDraftListResponse,
  SavedDraftPayload,
} from '../types/savedDraft';

const DRAFTS_URL = '/api/v1/mission-drafts';

// getMissionById and getMissionRoute lived here until #516. They called
// /api/v1/missions/{id} and /api/v1/missions/{id}/route, which the backend has
// never registered — and could not have served, having no Mission model and no
// table. Neither was reachable: the one call site read drone.currentMissionId,
// which toDrone hardcodes to null.
//
// Mission drafts below are the real, working half. Same file, similar names,
// entirely different fate.
export const missionService = {
  // --- Mission-draft persistence (#299 Feature G / #321) ---
  // Owner-scoped; the api-backend keys ownership on the operator's Dex sub via
  // the id_token the ApiClient already attaches. Callers surface 401/403/404/409
  // to the operator (see MissionPersistencePanel).

  /**
   * Create a new saved draft; the server assigns id and revision=1.
   *
   * `droneId` names the drone the draft is planned for (#403/#451). Sending it
   * subjects the draft to that drone's inclusion geofence, so an out-of-zone
   * route comes back as 409 GEOFENCE_VIOLATION instead of saving.
   */
  async saveDraft(
    name: string,
    payload: SavedDraftPayload,
    droneId?: string | null
  ): Promise<SavedDraft> {
    return apiClient.post<SavedDraft>(DRAFTS_URL, {
      name,
      payload,
      drone_id: droneId ?? undefined,
    });
  },

  /**
   * Update an existing draft. `revision` is the client's base revision for
   * optimistic-concurrency: a stale value rejects with 409 (thrown as ApiError).
   */
  async updateDraft(
    id: string,
    name: string,
    payload: SavedDraftPayload,
    revision: number,
    droneId?: string | null
  ): Promise<SavedDraft> {
    return apiClient.put<SavedDraft>(`${DRAFTS_URL}/${id}`, {
      name,
      payload,
      revision,
      drone_id: droneId ?? undefined,
    });
  },

  /** Fetch a single draft including its full payload. */
  async loadDraft(id: string): Promise<SavedDraft> {
    return apiClient.get<SavedDraft>(`${DRAFTS_URL}/${id}`);
  },

  /** List the operator's saved drafts (summaries, most-recently-updated first). */
  async listDrafts(): Promise<SavedDraftListResponse> {
    return apiClient.get<SavedDraftListResponse>(DRAFTS_URL);
  },

  /** Delete a saved draft. */
  async deleteDraft(id: string): Promise<void> {
    await apiClient.delete(`${DRAFTS_URL}/${id}`);
  },
};
